import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../../kuba.services';

import { AppraisalMeetingService } from '../meetings/services/appraisal-meeting.service';
import { AppraisalMeetingDto, AppraisalMeetingListItemDto } from '../meetings/models';
import { AppraisalTaskService } from '../tasks/services/appraisal-task.service';
import { AppraisalTaskDto, AppraisalTaskStatus, AppraisalTaskUpdateDto } from '../tasks/models';

type TaskRow = {
  Id: number;
  AppraisalMeetingId: number;
  ManagerUserId?: number | null;
  QuestionId?: number;
  Title: string;
  Description: string;
  QuestionText: string;
  Status: number;
  DueDate?: string;
  CompletedOn?: string;
  AssignedToUserId: number;
  AssignedToName: string;
  CreatedByUserId: number;
  CreatedByName: string;
  EmployeeName: string;
  AppraisalTitle: string;
};

@Component({
  selector: 'appraisals-tasks',
  templateUrl: './appraisals-tasks.component.html',
  styleUrls: ['./appraisals-tasks.component.scss'],
  standalone: false
})
export class AppraisalsTasksComponent implements OnInit, OnDestroy {
  @Input() isManager = false;
  @Input() canManageTasks = false;

  private readonly notifications = inject(NotificationService);
  @ViewChild('rowActionMenu', { static: false }) rowActionMenu?: Menu;

  readonly taskStatus = AppraisalTaskStatus;
  readonly currentUserId = +BaseServices.UserId;
  readonly isAdminUser = BaseServices.UserRole === 'Admin';

  tasks: TaskRow[] = [];
  private allTasks: TaskRow[] = [];
  selectedTasks: TaskRow[] = [];
  rowActionItems: MenuItem[] = [];

  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  first = 0;

  isLoading = false;
  isLoadingTaskDetail = false;
  isLoadingTaskEdit = false;
  isSavingTaskEdit = false;
  isDeletingSelectedTasks = false;
  showTaskDetailDialog = false;
  showTaskEditDialog = false;
  selectedTaskDetail: AppraisalTaskDto | null = null;
  selectedTaskEdit: AppraisalTaskDto | null = null;
  selectedTaskContext: TaskRow | null = null;
  taskActionStates: Record<number, 'completing' | 'deleting'> = {};
  taskAssignees: { id: number; name: string }[] = [];
  editTaskOriginalDueDate = '';
  readonly minTaskDueDate = this.toDateInputValue(new Date().toISOString());
  editTaskForm: {
    Title: string;
    Description: string;
    DueDate: string;
    AssignedToUserId: number | null;
  } = {
    Title: '',
    Description: '',
    DueDate: '',
    AssignedToUserId: null
  };

  employeeNameFilter = '';
  taskTitleFilter = '';
  appraisalTitleFilter = '';
  statusFilter: number | null = null;

  private filterChangeSubject = new Subject<void>();
  private subscriptions: Subscription[] = [];

  sortField: keyof TaskRow = 'DueDate';
  sortOrder: 'ASC' | 'DESC' = 'ASC';
  readonly descriptionPreviewLimit = 90;

  statusOptions: { label: string; value: number | null }[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private meetingService: AppraisalMeetingService,
    private taskService: AppraisalTaskService
  ) {
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(debounceTime(300))
        .subscribe(() => {
          this.currentPage = 1;
          this.first = 0;
          this.applyTableView();
        })
    );
  }

  ngOnInit(): void {
    this.initializeStatusOptions();
    this.loadTasks();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get canDeleteSelectedTasks(): boolean {
    return ( this.canManageTasks &&
      this.selectedTasks.length > 0 &&
      this.selectedTasks.every(task => this.canDeleteTask(task)) &&
      !this.isDeletingSelectedTasks &&
      !this.hasPendingTaskActions());
  }

  loadTasks(): void {
    this.isLoading = true;

    if (this.canManageTasks) {
      this.loadManagerTasks();
      return;
    }

    this.subscriptions.push(
      this.taskService.getByAssigneeId(this.currentUserId).subscribe({
        next: tasks => {
          this.allTasks = tasks.map(task => this.mapTaskRow(task));
          this.resetTaskActionState();
          this.applyTableView();
          this.isLoading = false;
        },
        error: () => {
          this.resetTaskActionState();
          this.isLoading = false;
          this.showError('Failed to load tasks');
        }
      })
    );
  }

  private loadManagerTasks(): void {
    const meetingFilter = {
      pageNumber: 1,
      pageSize: 500,
      sortColumn: 'CreatedOn',
      sortDirection: 'DESC' as const
    };

    this.subscriptions.push(
      this.meetingService
        .getPagedList(BaseServices.BusinessId, meetingFilter)
        .subscribe({
          next: response => {
            const meetings = response.Items || [];
            if (meetings.length === 0) {
              this.allTasks = [];
              this.resetTaskActionState();
              this.applyTableView();
              this.isLoading = false;
              return;
            }

            const meetingMap = new Map<number, AppraisalMeetingListItemDto>();
            meetings.forEach(meeting => meetingMap.set(meeting.Id, meeting));

            const requests = meetings.map(meeting =>
              this.taskService.getByMeetingId(meeting.Id).pipe(catchError(() => of([])))
            );

            this.subscriptions.push(
              forkJoin(requests).subscribe({
                next: taskGroups => {
                  this.allTasks = taskGroups
                    .flat()
                    .map(task => this.mapTaskRow(task, meetingMap.get(task.AppraisalMeetingId)));
                  this.resetTaskActionState();
                  this.applyTableView();
                  this.isLoading = false;
                },
                error: () => {
                  this.resetTaskActionState();
                  this.isLoading = false;
                  this.showError('Failed to load tasks');
                }
              })
            );
          },
          error: () => {
            this.resetTaskActionState();
            this.isLoading = false;
            this.showError('Failed to load tasks');
          }
        })
    );
  }

  mapTaskRow(
    task: AppraisalTaskDto,
    meeting?: AppraisalMeetingListItemDto
  ): TaskRow {
    return {
      Id: task.Id,
      AppraisalMeetingId: task.AppraisalMeetingId,
      ManagerUserId: meeting?.ManagerUserId ?? null,
      QuestionId: task.QuestionId,
      Title: task.Title,
      Description: task.Description || '',
      QuestionText: task.QuestionText || '',
      Status: task.Status,
      DueDate: task.DueDate,
      CompletedOn: task.CompletedOn,
      AssignedToUserId: task.AssignedToUserId,
      AssignedToName: task.AssignedToName || '',
      CreatedByUserId: task.CreatedByUserId,
      CreatedByName: task.CreatedByName || '',
      EmployeeName: task.EmployeeName || meeting?.EmployeeName || task.AssignedToName || '',
      AppraisalTitle:
        task.AppraisalMeetingTitle || meeting?.Title || meeting?.TemplateName || task.Title
    };
  }

  onPageChange(event: any): void {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.pageSize = event.rows;
    this.first = event.first;
    this.applyTableView();
  }

  onSort(event: any): void {
    this.sortField = this.mapSortField(event.field);
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';
    this.applyTableView();
  }

  onFilterChange(): void {
    this.filterChangeSubject.next();
  }

  clearFilters(): void {
    this.employeeNameFilter = '';
    this.taskTitleFilter = '';
    this.appraisalTitleFilter = '';
    this.statusFilter = null;
    this.onFilterChange();
  }

  private mapSortField(field: string): keyof TaskRow {
    switch (field) {
      case 'EmployeeName':
      case 'employeeName':
        return 'EmployeeName';
      case 'AppraisalTitle':
      case 'appraisalTitle':
        return 'AppraisalTitle';
      case 'Title':
      case 'taskTitle':
        return 'Title';
      case 'Status':
      case 'status':
        return 'Status';
      case 'Description':
      case 'description':
        return 'Description';
      case 'DueDate':
      case 'dueDate':
        return 'DueDate';
      default:
        return 'DueDate';
    }
  }

  private applyTableView(): void {
    let filtered = [...this.allTasks];

    if (this.employeeNameFilter.trim()) {
      const search = this.employeeNameFilter.toLowerCase();
      filtered = filtered.filter(task =>
        (task.EmployeeName || '').toLowerCase().includes(search)
      );
    }

    if (this.taskTitleFilter.trim()) {
      const search = this.taskTitleFilter.toLowerCase();
      filtered = filtered.filter(task =>
        [task.Title, task.Description, task.QuestionText]
          .join(' ')
          .toLowerCase()
          .includes(search)
      );
    }

    if (this.appraisalTitleFilter.trim()) {
      const search = this.appraisalTitleFilter.toLowerCase();
      filtered = filtered.filter(task =>
        (task.AppraisalTitle || '').toLowerCase().includes(search)
      );
    }

    if (this.statusFilter !== null) {
      filtered = filtered.filter(task => task.Status === this.statusFilter);
    }

    filtered.sort((leftTask, rightTask) => {
      const left = this.getSortableValue(leftTask, this.sortField);
      const right = this.getSortableValue(rightTask, this.sortField);

      if (left < right) return this.sortOrder === 'ASC' ? -1 : 1;
      if (left > right) return this.sortOrder === 'ASC' ? 1 : -1;
      return 0;
    });

    this.totalItems = filtered.length;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize);

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.tasks = filtered.slice(start, end);
  }

  private getSortableValue(task: TaskRow, field: keyof TaskRow): number | string {
    const value = task[field];
    if (field === 'DueDate' || field === 'CompletedOn') {
      return value ? new Date(value).getTime() : 0;
    }

    if (typeof value === 'number') {
      return value;
    }

    return (value || '').toString().toLowerCase();
  }

  viewTask(task: TaskRow): void {
    this.closeActionMenu();
    this.showTaskDetailDialog = true;
    this.selectedTaskDetail = null;
    this.selectedTaskContext = task;
    this.isLoadingTaskDetail = true;

    this.subscriptions.push(
      this.taskService.getById(task.Id).subscribe({
        next: taskDetail => {
          this.selectedTaskDetail = taskDetail;
          this.isLoadingTaskDetail = false;
        },
        error: () => {
          this.showTaskDetailDialog = false;
          this.selectedTaskContext = null;
          this.isLoadingTaskDetail = false;
          this.showError('Failed to load task details');
        }
      })
    );
  }

  closeTaskDialog(): void {
    this.showTaskDetailDialog = false;
    this.selectedTaskDetail = null;
    this.selectedTaskContext = null;
    this.isLoadingTaskDetail = false;
  }

  canEditTask(task?: { ManagerUserId?: number | null } | null): boolean {
    return this.canManageTaskAdministration(task);
  }

  openEditTask(task: TaskRow): void {
    if (!this.canEditTask(task) || this.isTaskActionPending(task.Id) || this.isDeletingSelectedTasks) {
      return;
    }

    this.closeActionMenu();
    this.closeTaskDialog();
    this.showTaskEditDialog = true;
    this.isLoadingTaskEdit = true;
    this.isSavingTaskEdit = false;
    this.selectedTaskEdit = null;
    this.taskAssignees = [];
    this.resetEditTaskForm();

    this.subscriptions.push(
      forkJoin({
        task: this.taskService.getById(task.Id),
        meeting: this.meetingService.getById(task.AppraisalMeetingId)
      }).subscribe({
        next: ({ task: taskDetail, meeting }) => {
          this.selectedTaskEdit = taskDetail;
          this.editTaskOriginalDueDate = this.toDateInputValue(taskDetail.DueDate);
          this.taskAssignees = this.buildTaskAssigneeOptions(meeting, taskDetail);
          this.editTaskForm = {
            Title: taskDetail.Title || '',
            Description: taskDetail.Description || '',
            DueDate: this.editTaskOriginalDueDate,
            AssignedToUserId: taskDetail.AssignedToUserId
          };
          this.isLoadingTaskEdit = false;
        },
        error: () => {
          this.closeTaskEditDialog();
          this.showError('Failed to load task edit form');
        }
      })
    );
  }

  closeTaskEditDialog(): void {
    this.showTaskEditDialog = false;
    this.isLoadingTaskEdit = false;
    this.isSavingTaskEdit = false;
    this.selectedTaskEdit = null;
    this.taskAssignees = [];
    this.editTaskOriginalDueDate = '';
    this.resetEditTaskForm();
  }

  saveTaskEdit(): void {
    if (!this.selectedTaskEdit || !this.canEditTask() || this.isSavingTaskEdit) {
      return;
    }

    const title = this.editTaskForm.Title.trim();
    if (!title || !this.editTaskForm.AssignedToUserId || this.isEditDueDateInvalid()) {
      this.showError('Failed to save task');
      return;
    }

    this.isSavingTaskEdit = true;

    const dto: AppraisalTaskUpdateDto = {
      Title: title,
      Description: this.editTaskForm.Description.trim() || undefined,
      DueDate: this.editTaskForm.DueDate
        ? new Date(this.editTaskForm.DueDate).toISOString()
        : undefined,
      AssignedToUserId: this.editTaskForm.AssignedToUserId
    };

    this.subscriptions.push(
      this.taskService.update(this.selectedTaskEdit.Id, dto).subscribe({
        next: () => {
          this.showSuccess('Task updated successfully');
          this.closeTaskEditDialog();
          this.loadTasks();
        },
        error: () => {
          this.isSavingTaskEdit = false;
          this.showError('Failed to update task');
        }
      })
    );
  }

  openLinkedAppraisal(): void {
    if (!this.selectedTaskDetail) {
      return;
    }

    const meetingId = this.selectedTaskDetail.AppraisalMeetingId;
    this.closeTaskDialog();
    this.router.navigate(['./meetings', meetingId], { relativeTo: this.route });
  }

  canCompleteTask(task: TaskRow): boolean {
    return (
      task.Status !== AppraisalTaskStatus.Done &&
      (
        this.isAdminUser ||
        task.ManagerUserId === this.currentUserId ||
        task.AssignedToUserId === this.currentUserId
      )
    );
  }

  completeTask(task: TaskRow): void {
    if (!this.canCompleteTask(task) || this.isTaskActionPending(task.Id) || this.isDeletingSelectedTasks) {
      return;
    }

    this.closeActionMenu();
    this.confirmationService.confirm({
      message: this.translate.instant('APPRAISAL_CONFIRM_COMPLETE_ACTION_POINT'),
      accept: () => {
        this.setTaskActionState(task.Id, 'completing');
        this.taskService.updateStatus(task.Id, { Status: AppraisalTaskStatus.Done }).subscribe({
          next: () => {
            this.showSuccess('Task completed successfully');
            this.closeTaskDialog();
            this.loadTasks();
          },
          error: () => {
            this.clearTaskActionState(task.Id);
            this.showError('Failed to complete task');
          }
        });
      }
    });
  }

  canDeleteTask(task?: { Id: number; ManagerUserId?: number | null } | null): boolean {
    return ( this.canManageTaskAdministration(task) &&
      !this.isDeletingSelectedTasks &&
      (!task || !this.isTaskActionPending(task.Id)));
  }

  deleteTask(task: TaskRow): void {
    if (!this.canDeleteTask(task)) {
      return;
    }

    this.closeActionMenu();
    this.confirmationService.confirm({
      message: this.translate.instant('APPRAISAL_CONFIRM_DELETE_ACTION_POINT'),
      accept: () => {
        this.setTaskActionState(task.Id, 'deleting');
        this.taskService.delete(task.Id).subscribe({
          next: () => {
            this.showSuccess('Task deleted successfully');
            this.closeTaskDialog();
            this.loadTasks();
          },
          error: () => {
            this.clearTaskActionState(task.Id);
            this.showError('Failed to delete task');
          }
        });
      }
    });
  }

  deleteSelectedTasks(): void {
    if (!this.canDeleteSelectedTasks || this.selectedTasks.length === 0) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('APPRAISAL_CONFIRM_DELETE_ACTION_POINTS', {
        count: this.selectedTasks.length
      }),
      accept: () => {
        this.isDeletingSelectedTasks = true;
        const deleteRequests = this.selectedTasks.map(task => this.taskService.delete(task.Id));
        this.subscriptions.push(
          forkJoin(deleteRequests).subscribe({
            next: () => {
              this.showSuccess(`${this.selectedTasks.length} tasks deleted successfully`);
              this.selectedTasks = [];
              this.loadTasks();
            },
            error: () => {
              this.isDeletingSelectedTasks = false;
              this.showError('Failed to delete selected tasks');
            }
          })
        );
      }
    });
  }

  getStatusClass(status: number): string {
    const statusMap: { [key: number]: string } = {
      [AppraisalTaskStatus.Open]: 'badge-secondary',
      [AppraisalTaskStatus.InProgress]: 'badge-primary',
      [AppraisalTaskStatus.Done]: 'badge-success'
    };

    return statusMap[status] || 'badge-secondary';
  }

  getStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      [AppraisalTaskStatus.Open]: 'OPEN',
      [AppraisalTaskStatus.InProgress]: 'IN_PROGRESS',
      [AppraisalTaskStatus.Done]: 'DONE'
    };

    return statusMap[status] || 'OPEN';
  }

  isTaskOverdue(task: TaskRow): boolean {
    return (
      task.Status !== AppraisalTaskStatus.Done &&
      !!task.DueDate &&
      new Date(task.DueDate) < new Date()
    );
  }

  getTaskContext(task: TaskRow): string {
    return task.QuestionText || task.AppraisalTitle || '';
  }

  getDescriptionPreview(description?: string, maxLength: number = this.descriptionPreviewLimit): string {
    const normalizedDescription = (description || '').replace(/\s+/g, ' ').trim();
    if (!normalizedDescription) {
      return '—';
    }

    if (normalizedDescription.length <= maxLength) {
      return normalizedDescription;
    }

    return `${normalizedDescription.slice(0, maxLength).trim()}...`;
  }

  hasTruncatedDescription(description?: string, maxLength: number = this.descriptionPreviewLimit): boolean {
    return (((description || '').replace(/\s+/g, ' ').trim().length || 0) > maxLength
    );
  }

  showSuccess(_message: string): void {
    this.notifications.success('UI_NOTIFICATIONS.SAVED');
  }

  showError(_message: string): void {
    this.notifications.success('UI_NOTIFICATIONS.DELETED');
  }

  openActionMenu(menu: Menu, task: TaskRow, event: MouseEvent): void {
    if (this.isTaskActionPending(task.Id) || this.isDeletingSelectedTasks) {
      return;
    }

    event.stopPropagation();
    this.rowActionItems = this.buildRowActionItems(task);
    menu.toggle(event);
  }

  closeActionMenu(): void {
    this.rowActionMenu?.hide();
  }

  private initializeStatusOptions(): void {
    this.statusOptions = [
      { label: this.translate.instant('ALL_STATUSES'), value: null },
      { label: this.translate.instant('OPEN'), value: AppraisalTaskStatus.Open },
      { label: this.translate.instant('IN_PROGRESS'), value: AppraisalTaskStatus.InProgress },
      { label: this.translate.instant('DONE'), value: AppraisalTaskStatus.Done }
    ];
  }

  private resetEditTaskForm(): void {
    this.editTaskForm = {
      Title: '',
      Description: '',
      DueDate: '',
      AssignedToUserId: null
    };
  }

  private buildTaskAssigneeOptions(
    meeting: AppraisalMeetingDto,
    task: AppraisalTaskDto
  ): { id: number; name: string }[] {
    const options = [
      { id: meeting.ManagerUserId, name: meeting.ManagerName || 'Manager' },
      { id: meeting.EmployeeUserId, name: meeting.EmployeeName || 'Employee' }
    ];

    if (
      task.AssignedToUserId &&
      !options.some(option => option.id === task.AssignedToUserId)
    ) {
      options.push({
        id: task.AssignedToUserId,
        name: task.AssignedToName || `User #${task.AssignedToUserId}`
      });
    }

    const seen = new Set<number>();
    return options.filter(option => {
      if (seen.has(option.id)) {
        return false;
      }

      seen.add(option.id);
      return true;
    });
  }

  private toDateInputValue(isoDate?: string): string {
    if (!isoDate) {
      return '';
    }

    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return '';
    }

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private isEditDueDateInvalid(): boolean {
    if (!this.editTaskForm.DueDate) {
      return false;
    }

    if (this.editTaskForm.DueDate >= this.minTaskDueDate) {
      return false;
    }

    return this.editTaskForm.DueDate !== this.editTaskOriginalDueDate;
  }

  private buildRowActionItems(task: TaskRow): MenuItem[] {
    const items: MenuItem[] = [
      {
        label: this.translate.instant('VIEW'),
        icon: 'pi pi-eye kuba-icon-info',
        styleClass: 'view-item',
        command: () => this.viewTask(task)
      }
    ];

    if (this.canEditTask(task)) {
      items.push({
        label: this.translate.instant('EDIT'),
        icon: 'pi pi-pencil kuba-icon-warn',
        styleClass: 'edit-item',
        command: () => this.openEditTask(task)
      });
    }

    if (this.canCompleteTask(task)) {
      items.push({
        label: this.translate.instant('COMPLETE'),
        icon: 'pi pi-check kuba-icon-success',
        command: () => this.completeTask(task),
        styleClass: 'complete-item'
      });
    }

    if (this.canDeleteTask(task)) {
      items.push({
        label: this.translate.instant('DELETE'),
        icon: 'pi pi-trash kuba-icon-danger',
        command: () => this.deleteTask(task),
        styleClass: 'danger-item'
      });
    }

    return items;
  }

  isTaskActionPending(taskId: number): boolean {
    return !!this.taskActionStates[taskId];
  }

  isTaskDeleting(taskId: number): boolean {
    return this.taskActionStates[taskId] === 'deleting';
  }

  isTaskCompleting(taskId: number): boolean {
    return this.taskActionStates[taskId] === 'completing';
  }

  hasPendingTaskActions(): boolean {
    return Object.keys(this.taskActionStates).length > 0;
  }

  private setTaskActionState(taskId: number, state: 'completing' | 'deleting'): void {
    this.taskActionStates = {
      ...this.taskActionStates,
      [taskId]: state
    };
  }

  private clearTaskActionState(taskId: number): void {
    const { [taskId]: _removed, ...rest } = this.taskActionStates;
    this.taskActionStates = rest;
  }

  private resetTaskActionState(): void {
    this.taskActionStates = {};
    this.isDeletingSelectedTasks = false;
  }

  private canManageTaskAdministration(
    task?: { ManagerUserId?: number | null } | null
  ): boolean {
    if (!this.canManageTasks) {
      return false;
    }

    if (this.isAdminUser) {
      return true;
    }

    return !!task && task.ManagerUserId === this.currentUserId;
  }
}
